import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Search, Plus, X, Printer, User, Car, Trash2, Check } from "lucide-react";
import { api } from "../../lib/api";
import ThermalReceipt from "../../components/ThermalReceipt";
import UltimasCompras from "../../components/UltimasCompras";
import { toast } from "sonner";

const money = (v) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

export default function NovaVenda() {
  const { unidade, theme } = useOutletContext();
  const [estoque, setEstoque] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [config, setConfig] = useState({ percentual_desconto_teto: 30 });

  const [placa, setPlaca] = useState("");
  const [cliente, setCliente] = useState(null);
  const [novoCliente, setNovoCliente] = useState({
    nome: "",
    telefone: "",
    celular: "",
    cpf: "",
    modelo_veiculo: "",
  });
  const [kmAtual, setKmAtual] = useState("");

  const [vendedorId, setVendedorId] = useState("");
  const [busca, setBusca] = useState("");
  const [carrinho, setCarrinho] = useState([]);
  const [desconto, setDesconto] = useState(0);

  const [finalizando, setFinalizando] = useState(false);
  const [vendaConcluida, setVendaConcluida] = useState(null);

  const receiptRef = useRef(null);

  const load = async () => {
    const [est, vs, cfg] = await Promise.all([
      api.get("/estoque", { params: { unidade_id: unidade.id } }),
      api.get("/vendedores", { params: { unidade_id: unidade.id } }),
      api.get("/configuracoes"),
    ]);
    setEstoque(est.data);
    setVendedores(vs.data);
    setConfig(cfg.data);
  };

  useEffect(() => {
    load();
  }, [unidade.id]); // eslint-disable-line

  const buscarPlaca = async () => {
    const p = placa.trim();
    if (!p) return;
    try {
      const res = await api.get(`/clientes/by-placa/${encodeURIComponent(p)}`);
      setCliente(res.data);
      setKmAtual(String(res.data.km_ultima_troca || ""));
      toast.success(`Cliente: ${res.data.nome}`);
    } catch {
      setCliente(null);
      toast.info("Placa não cadastrada. Preencha os dados do novo cliente.");
    }
  };

  const limparCliente = () => {
    setCliente(null);
    setPlaca("");
    setNovoCliente({ nome: "", telefone: "", celular: "", cpf: "", modelo_veiculo: "" });
    setKmAtual("");
  };

  const produtosFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return estoque.slice(0, 12);
    return estoque.filter(
      (e) =>
        e.produto_nome.toLowerCase().includes(q) ||
        (e.categoria || "").toLowerCase().includes(q)
    );
  }, [estoque, busca]);

  const addItem = (item) => {
    if (item.quantidade_atual <= 0) {
      toast.error("Produto sem estoque");
      return;
    }
    setCarrinho((prev) => {
      const existe = prev.find((p) => p.produto_id === item.produto_id);
      if (existe) {
        if (existe.quantidade >= item.quantidade_atual) {
          toast.error("Estoque insuficiente");
          return prev;
        }
        return prev.map((p) =>
          p.produto_id === item.produto_id
            ? { ...p, quantidade: p.quantidade + 1 }
            : p
        );
      }
      return [
        ...prev,
        {
          produto_id: item.produto_id,
          nome: item.produto_nome,
          preco_unitario: item.preco_venda,
          preco_custo: item.preco_custo ?? item.preco_custo_global,
          quantidade: 1,
          estoque_disp: item.quantidade_atual,
        },
      ];
    });
  };

  const alterarQtd = (pid, delta) => {
    setCarrinho((prev) =>
      prev
        .map((p) => {
          if (p.produto_id !== pid) return p;
          const novaQ = p.quantidade + delta;
          if (novaQ <= 0) return null;
          if (novaQ > p.estoque_disp) {
            toast.error("Estoque insuficiente");
            return p;
          }
          return { ...p, quantidade: novaQ };
        })
        .filter(Boolean)
    );
  };

  const removerItem = (pid) =>
    setCarrinho((prev) => prev.filter((p) => p.produto_id !== pid));

  const totalBruto = useMemo(
    () => carrinho.reduce((s, i) => s + i.preco_unitario * i.quantidade, 0),
    [carrinho]
  );
  const descontoValor = useMemo(
    () => Number(((totalBruto * Number(desconto || 0)) / 100).toFixed(2)),
    [totalBruto, desconto]
  );
  const totalLiquido = Math.max(0, totalBruto - descontoValor);

  const descontoAcima = Number(desconto || 0) > config.percentual_desconto_teto;

  const podeFinalizar =
    carrinho.length > 0 &&
    vendedorId &&
    !descontoAcima &&
    (cliente || (novoCliente.nome && placa));

  const finalizar = async () => {
    if (finalizando) return;
    if (!podeFinalizar) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    setFinalizando(true);
    try {
      const itens = carrinho.map((i) => ({
        produto_id: i.produto_id,
        nome: i.nome,
        quantidade: i.quantidade,
        preco_unitario: i.preco_unitario,
        preco_custo: i.preco_custo,
        subtotal: Number((i.preco_unitario * i.quantidade).toFixed(2)),
      }));
      const payload = {
        unidade_id: unidade.id,
        vendedor_id: vendedorId,
        cliente_id: cliente?.id,
        cliente_dados: cliente
          ? null
          : {
              nome: novoCliente.nome,
              telefone: novoCliente.telefone,
              celular: novoCliente.celular,
              cpf: novoCliente.cpf,
              placa,
              modelo_veiculo: novoCliente.modelo_veiculo,
              km_ultima_troca: Number(kmAtual || 0),
              unidade_id: unidade.id,
            },
        itens,
        total_bruto: Number(totalBruto.toFixed(2)),
        total_desconto: Number(descontoValor.toFixed(2)),
        percentual_desconto: Number(desconto || 0),
        km_atual: kmAtual ? Number(kmAtual) : null,
      };
      const res = await api.post("/vendas", payload);
      setVendaConcluida(res.data);
      toast.success("Venda finalizada!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao finalizar venda");
    } finally {
      setFinalizando(false);
    }
  };

  const imprimir = () => {
    window.print();
  };

  const novaVenda = () => {
    setCarrinho([]);
    setDesconto(0);
    setPlaca("");
    setCliente(null);
    setNovoCliente({ nome: "", telefone: "", celular: "", cpf: "", modelo_veiculo: "" });
    setKmAtual("");
    setVendedorId("");
    setBusca("");
    setVendaConcluida(null);
  };

  if (vendaConcluida) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6 print:hidden">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
              Venda concluída
            </p>
            <h2 className="font-display text-3xl font-black tracking-tighter">
              Cupom de Venda
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="venda-imprimir-btn"
              onClick={imprimir}
              className={`${theme.primary} h-12 px-5 font-display font-bold uppercase text-sm tracking-widest flex items-center gap-2 cta-brutal`}
            >
              <Printer className="w-4 h-4" /> Imprimir
            </button>
            <button
              data-testid="venda-nova-btn"
              onClick={novaVenda}
              className="h-12 px-5 border-2 border-neutral-900 bg-white hover:bg-neutral-100 font-display font-bold uppercase text-sm tracking-widest cta-brutal"
            >
              Nova venda
            </button>
          </div>
        </div>
        <div ref={receiptRef}>
          <ThermalReceipt venda={vendaConcluida} unidade={unidade} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">
          Terminal PDV
        </p>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Nova Venda
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ESQUERDA */}
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente / Placa */}
          <section className="bg-white border-2 border-neutral-900 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg uppercase flex items-center gap-2">
                <Car className="w-5 h-5" /> Veículo & Cliente
              </h3>
              {cliente && (
                <button
                  onClick={limparCliente}
                  data-testid="limpar-cliente-btn"
                  className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-red-600 flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Limpar
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Placa
                </label>
                <div className="flex gap-2 mt-1">
                  <input
                    data-testid="placa-input"
                    value={placa}
                    onChange={(e) =>
                      setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
                    }
                    onKeyDown={(e) => e.key === "Enter" && buscarPlaca()}
                    placeholder="ABC1D23"
                    className="flex-1 h-11 px-3 border-2 border-neutral-900 font-mono text-lg tracking-widest uppercase focus:outline-none focus:ring-2 ring-offset-0"
                  />
                  <button
                    data-testid="placa-buscar-btn"
                    onClick={buscarPlaca}
                    className={`h-11 px-4 ${theme.primary} font-display font-bold uppercase text-xs tracking-widest flex items-center gap-2`}
                  >
                    <Search className="w-4 h-4" /> Buscar
                  </button>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  KM Atual
                </label>
                <input
                  data-testid="km-input"
                  type="number"
                  value={kmAtual}
                  onChange={(e) => setKmAtual(e.target.value)}
                  placeholder="0"
                  className="w-full mt-1 h-11 px-3 border-2 border-neutral-900 font-mono text-lg"
                />
              </div>
            </div>

            {cliente ? (
              <div className={`mt-4 p-4 ${theme.softBg} border-2 ${theme.border}`}>
                <div className="font-display font-bold text-lg">{cliente.nome}</div>
                <div className="text-sm text-neutral-700">
                  {cliente.modelo_veiculo}
                  {cliente.celular && <> · {cliente.celular}</>}
                  {!cliente.celular && cliente.telefone && <> · {cliente.telefone}</>}
                </div>
                {cliente.cpf && (
                  <div className="font-mono text-xs text-neutral-600">
                    CPF: {cliente.cpf}
                  </div>
                )}
                <div className="font-mono text-xs mt-1 text-neutral-600">
                  KM Última Troca: {cliente.km_ultima_troca?.toLocaleString("pt-BR")}
                </div>
                <div className="mt-3 bg-white border border-neutral-300 p-2">
                  <UltimasCompras compras={cliente.ultimas_compras} />
                </div>
              </div>
            ) : placa ? (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    data-testid="novo-cliente-nome"
                    value={novoCliente.nome}
                    onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })}
                    placeholder="Nome do cliente *"
                    className="h-11 px-3 border-2 border-neutral-900"
                  />
                  <input
                    data-testid="novo-cliente-celular"
                    value={novoCliente.celular}
                    onChange={(e) =>
                      setNovoCliente({
                        ...novoCliente,
                        celular: e.target.value.replace(/[^0-9]/g, ""),
                      })
                    }
                    placeholder="Celular"
                    className="h-11 px-3 border-2 border-neutral-900 font-mono"
                  />
                  <input
                    data-testid="novo-cliente-cpf"
                    value={novoCliente.cpf}
                    onChange={(e) =>
                      setNovoCliente({
                        ...novoCliente,
                        cpf: e.target.value.replace(/[^0-9]/g, "").slice(0, 11),
                      })
                    }
                    placeholder="CPF"
                    className="h-11 px-3 border-2 border-neutral-900 font-mono"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    data-testid="novo-cliente-telefone"
                    value={novoCliente.telefone}
                    onChange={(e) =>
                      setNovoCliente({ ...novoCliente, telefone: e.target.value })
                    }
                    placeholder="Telefone fixo (opcional)"
                    className="h-11 px-3 border-2 border-neutral-900"
                  />
                  <input
                    data-testid="novo-cliente-modelo"
                    value={novoCliente.modelo_veiculo}
                    onChange={(e) =>
                      setNovoCliente({ ...novoCliente, modelo_veiculo: e.target.value })
                    }
                    placeholder="Modelo do veículo"
                    className="h-11 px-3 border-2 border-neutral-900"
                  />
                </div>
              </div>
            ) : null}
          </section>

          {/* Vendedor */}
          <section className="bg-white border-2 border-neutral-900 p-5">
            <h3 className="font-display font-bold text-lg uppercase mb-3 flex items-center gap-2">
              <User className="w-5 h-5" /> Vendedor
            </h3>
            <select
              data-testid="vendedor-select"
              value={vendedorId}
              onChange={(e) => setVendedorId(e.target.value)}
              className="w-full h-12 px-3 border-2 border-neutral-900 font-display font-bold text-base bg-white"
            >
              <option value="">— Selecione o vendedor —</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.nome}
                </option>
              ))}
            </select>
          </section>

          {/* Busca Produtos */}
          <section className="bg-white border-2 border-neutral-900 p-5">
            <h3 className="font-display font-bold text-lg uppercase mb-3">Produtos</h3>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                data-testid="produto-busca-input"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar produto por nome ou categoria..."
                className="w-full h-12 pl-10 pr-3 border-2 border-neutral-900"
              />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto">
              {produtosFiltrados.length === 0 && (
                <div className="col-span-full text-sm text-neutral-500 p-4 text-center">
                  Nenhum produto encontrado
                </div>
              )}
              {produtosFiltrados.map((p) => (
                <button
                  key={p.id}
                  data-testid={`produto-item-${p.produto_id}`}
                  onClick={() => addItem(p)}
                  disabled={p.quantidade_atual <= 0}
                  className="text-left p-3 border border-neutral-300 hover:border-neutral-900 hover:shadow-brutal-sm transition-all bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="font-display font-bold text-sm leading-tight">
                    {p.produto_nome}
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {p.categoria} · {p.quantidade_atual} un.
                    </span>
                    <span className="font-mono text-sm font-bold">
                      {money(p.preco_venda)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        </div>

        {/* DIREITA - Carrinho */}
        <aside className="bg-white border-2 border-neutral-900 p-5 h-fit lg:sticky lg:top-4">
          <h3 className="font-display font-bold text-lg uppercase mb-3">Carrinho</h3>
          {carrinho.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-500">
              Adicione produtos para iniciar
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {carrinho.map((it) => (
                <div
                  key={it.produto_id}
                  data-testid={`carrinho-item-${it.produto_id}`}
                  className="border border-neutral-300 p-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-sm font-bold leading-tight flex-1">
                      {it.nome}
                    </div>
                    <button
                      onClick={() => removerItem(it.produto_id)}
                      className="text-neutral-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => alterarQtd(it.produto_id, -1)}
                        className="w-7 h-7 border border-neutral-900 font-mono font-bold hover:bg-neutral-100"
                      >
                        -
                      </button>
                      <span className="font-mono font-bold w-8 text-center">{it.quantidade}</span>
                      <button
                        onClick={() => alterarQtd(it.produto_id, 1)}
                        className="w-7 h-7 border border-neutral-900 font-mono font-bold hover:bg-neutral-100"
                      >
                        <Plus className="w-3 h-3 inline" />
                      </button>
                    </div>
                    <div className="font-mono font-bold text-sm">
                      {money(it.preco_unitario * it.quantidade)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t-2 border-neutral-900 space-y-2">
            <div className="flex justify-between font-mono text-sm">
              <span className="uppercase tracking-widest text-xs text-neutral-500">Subtotal</span>
              <span data-testid="total-bruto">{money(totalBruto)}</span>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Desconto (%) — Teto: {config.percentual_desconto_teto}%
              </label>
              <input
                data-testid="desconto-input"
                type="number"
                min="0"
                max="100"
                value={desconto}
                onChange={(e) => setDesconto(e.target.value)}
                className={`w-full mt-1 h-10 px-3 border-2 font-mono ${
                  descontoAcima ? "border-red-600 bg-red-50" : "border-neutral-900"
                }`}
              />
              {descontoAcima && (
                <div data-testid="desconto-erro" className="text-xs text-red-600 mt-1 font-mono uppercase tracking-widest">
                  Desconto acima do permitido
                </div>
              )}
            </div>

            <div className="flex justify-between font-mono text-sm text-red-600">
              <span className="uppercase tracking-widest text-xs">Desconto</span>
              <span data-testid="desconto-valor">- {money(descontoValor)}</span>
            </div>
            <div className="flex justify-between items-end pt-2 border-t border-dashed border-neutral-400">
              <span className="font-display font-bold uppercase">Total</span>
              <span data-testid="total-liquido" className="font-mono font-bold text-2xl">
                {money(totalLiquido)}
              </span>
            </div>

            <button
              data-testid="finalizar-venda-btn"
              onClick={finalizar}
              disabled={!podeFinalizar || finalizando}
              className={`mt-4 w-full h-14 ${theme.primary} font-display font-bold text-base uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed cta-brutal`}
            >
              <Check className="w-5 h-5" />
              {finalizando ? "Processando..." : "Finalizar & Imprimir"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
